import { AuthProvider, useAuth } from "./auth/AuthContext";
import NavBar from "./components/NavBar/NavBar";
import { CustomThemeProvider } from "./contexts/CustomThemeContext";
import { Box, Spinner, useColorModeValue } from "@chakra-ui/react";
import MobileFooterNavBar from "./components/NavBar/MobileFooterNavBar";
import MobileSideBar from "./components/SideBar/MobileSideBar";
import AppRoutes from "./routes/AppRoutes";
import PwaInstallPromptButton from "./components/PwaButton/PwaInstallPromptButton";
import { Navigate, useLocation } from "react-router-dom";
import { VehicleProvider } from "./contexts/VehicleContext";
import { DeliveryAddressProvider } from "./deliveryModule/DeliveryAddressProvider";
import StickyCartButton from "./components/Cart/StickyCartButton";

const MOBILE_FOOTER_HEIGHT = "62px";
const MOBILE_SAFE_AREA_BOTTOM = "env(safe-area-inset-bottom)";

const ADMIN_PROTECTED_ROUTES = [
  "/admin",
  "/analytics",
  "/manage/products",
  "/manage/inventory",

  "/finance/admin",

  "/drivers/admin",
  "/drivers/assignments",

  "/pins/manage",
  "/pins/verify",

  "/hike-manager",
  "/travel-manager",
  "/hire-manager",

  "/travel/car-maintenance",
  "/travel/accommodation-manager",
  "/travel/Accommodation-manager",
];

const isProtectedAdminPath = (pathname: string) =>
  ADMIN_PROTECTED_ROUTES.some((route) => {
    const cleanRoute = route.replace(/\/+$/, "");

    return pathname === cleanRoute || pathname.startsWith(`${cleanRoute}/`);
  });

const AppShell = () => {
  const location = useLocation();

  const authContext = useAuth() as any;
  const user = authContext?.user;
  const loading = authContext?.loading || authContext?.isLoading || false;

  const headerFooterBg = useColorModeValue("teal.500", "teal.600");
  const headerFooterColor = useColorModeValue("white", "white");
  const contentBg = useColorModeValue("#f4f4f4", "gray.900");

  const isAdminRoute = isProtectedAdminPath(location.pathname);

  if (loading) {
    return (
      <Box
        height="100vh"
        width="100vw"
        display="flex"
        alignItems="center"
        justifyContent="center"
        bg={contentBg}
        sx={{
          height: "100dvh",
          minHeight: "100svh",
        }}
      >
        <Spinner size="lg" color="teal.400" />
      </Box>
    );
  }

  /**
   * TEMPORARY META/FACEBOOK REVIEW MODE
   *
   * What this does now:
   * - Maintenance mode is temporarily OFF.
   * - The full public app is available to logged-out users.
   * - /dashboard is public for Meta review.
   * - All Meta review pages are public:
   *   - /meta-review
   *   - /meta-review/callback
   *   - /meta-review/dashboard
   * - Only admin/internal pages require login.
   *
   * REVERT AFTER META REVIEW:
   *
   * 1. Re-import ComingSoon:
   *
   *    import ComingSoon from "./components/Maintenance/ComingSoon";
   *
   * 2. Bring back the public routes and staging/maintenance logic.
   *
   * 3. Restore the ComingSoon block for logged-out public visitors.
   */
  if (!user && isAdminRoute) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  return (
    <CustomThemeProvider>
      <VehicleProvider>
        <DeliveryAddressProvider>
          <Box
            height="100vh"
            width="100vw"
            overflow="hidden"
            display="flex"
            flexDirection="column"
            position="relative"
            sx={{
              height: "100dvh",
              minHeight: "100svh",
              "@supports (-webkit-touch-callout: none)": {
                minHeight: "-webkit-fill-available",
              },
            }}
          >
            {/* Header */}
            <Box
              as="header"
              height="60px"
              color={headerFooterColor}
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              width="100%"
              flexShrink={0}
              zIndex="10"
              boxShadow="0 4px 6px rgba(0, 0, 0, 0.1)"
            >
              <Box width={"100%"} display={{ base: "block", md: "none" }}>
                <MobileSideBar />
              </Box>

              <Box width={"100%"} display={{ base: "none", md: "block" }}>
                <NavBar />
              </Box>
            </Box>

            {/* Scrollable Content */}
            <Box
              flex="1"
              overflowY="auto"
              backgroundColor={contentBg}
              pb={{
                base: `calc(${MOBILE_FOOTER_HEIGHT} + ${MOBILE_SAFE_AREA_BOTTOM})`,
                md: 0,
              }}
              sx={{
                WebkitOverflowScrolling: "touch",
                overscrollBehaviorY: "contain",
                scrollPaddingBottom: `calc(${MOBILE_FOOTER_HEIGHT} + ${MOBILE_SAFE_AREA_BOTTOM})`,
              }}
            >
              <AppRoutes />
            </Box>

            {/* Footer */}
            <Box
              as="footer"
              position={{ base: "fixed", md: "static" }}
              left={0}
              right={0}
              bottom={0}
              height={{
                base: `calc(${MOBILE_FOOTER_HEIGHT} + ${MOBILE_SAFE_AREA_BOTTOM})`,
                md: MOBILE_FOOTER_HEIGHT,
              }}
              bg={headerFooterBg}
              color={headerFooterColor}
              display={{ base: "flex", md: "none" }}
              alignItems="stretch"
              justifyContent="center"
              width="100%"
              flexShrink={0}
              zIndex={1300}
              sx={{
                WebkitTransform: "translateZ(0)",
                transform: "translateZ(0)",
                willChange: "transform",
              }}
            >
              <Box w={"100%"} h="100%" bg="#F8FAFC">
                <MobileFooterNavBar />
              </Box>
            </Box>

            <StickyCartButton />

            {/* 💡 Custom Install Button */}
            {/* <PwaInstallPromptButton /> */}
          </Box>
        </DeliveryAddressProvider>
      </VehicleProvider>
    </CustomThemeProvider>
  );
};

function App() {
  return (
    <AuthProvider>
      <AppShell />
    </AuthProvider>
  );
}

export default App;
